import {
  PermissionViewModel,
  SharedComponent,
  ShareUserViewModel,
  UsersSuggestionServiceI,
} from '@dntlab/share-files-dialog';
import { catchError, EMPTY, map, Observable } from 'rxjs';
import AppSettings from '../common/app.settings';
import FieldsConstants from '../common/fields.constants';
import {
  AccountStatus,
  QueryMode,
  DefaultService as RestApiService,
  SortAttributeNames,
  SuggestionItemTypes,
  SuggestionResponse,
  SuggestionResult,
  SuggestionResultItem,
} from '../swagger-api';

export abstract class UsersSuggestionBaseService implements UsersSuggestionServiceI {
  protected constructor(protected readonly apiService: RestApiService) {}

  public abstract suggestUsers(dialog: SharedComponent, keyword: string): Promise<ShareUserViewModel[]>;

  protected fetchUsers(
    dialog: SharedComponent,
    keyword: string,
    excludedUsersIds: string[],
  ): Observable<ShareUserViewModel[]> {
    return this.apiService
      .restSearchSuggestGet(
        SuggestionItemTypes.Users,
        keyword,
        excludedUsersIds,
        SortAttributeNames.Score,
        false,
        QueryMode.Names,
        FieldsConstants.PAGINABLE_SEARCH_RESULT_COUNT,
        // Pagination is not supported yet
        0,
      )
      .pipe(
        catchError(() => EMPTY),
        map((response: SuggestionResponse) =>
          this.searchResultToUserViewModelAdapter(
            (dialog['currentPermissionViewModels'] as PermissionViewModel[]) ?? [],
            response,
          ),
        ),
      );
  }

  /**
   * @description Adapts search results to ShareUserViewModel list.
   * @param { PermissionViewModel[] } permissionViewModels
   * @param { SearchResponse } response
   * @returns { ShareUserViewModel[] }
   */
  protected searchResultToUserViewModelAdapter(
    permissionViewModels: PermissionViewModel[],
    response: SuggestionResponse,
  ): ShareUserViewModel[] {
    return this.filterAlreadySharedWithUsers(
      permissionViewModels,
      response.suggestion_results
        .map((result: SuggestionResult) => result.item)
        .map((user: SuggestionResultItem) => this.getShareUser(user)),
    );
  }

  /**
   * @description Forms share user from user.
   * @param { User } user
   * @returns { ShareUserViewModel }
   */
  private getShareUser(user: SuggestionResultItem): ShareUserViewModel {
    return {
      id: user.entity_id,
      displayName: user.display_name,
      userStatus: AccountStatus.ACTIVE,
      imageUrl: user.image_url || AppSettings.NO_PHOTO_DEFAULT_IMAGE,
      email: '',
    };
  }

  /**
   * @description Filters users form suggestedUsers list with whom file has already been
   *              shared if there's a filesToShare prop in dialogArgs.
   * @param { ShareUserViewModel[] } suggestedUsers
   * @param { PermissionViewModel[] } permissionViewModels
   * @returns { ShareUserViewModel[] }
   */
  private filterAlreadySharedWithUsers(
    permissionViewModels: PermissionViewModel[],
    suggestedUsers: ShareUserViewModel[],
  ): ShareUserViewModel[] {
    if (permissionViewModels.length) {
      suggestedUsers = suggestedUsers.filter(
        (user: ShareUserViewModel) =>
          !permissionViewModels.find((permission: PermissionViewModel) => permission.user.id === user.id),
      );
    }

    return suggestedUsers;
  }
}
